import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Pin, TriangleAlert } from 'lucide-react';
import { EMPTY_STATES, ROLES } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Button, Card, Chip, EmptyState, ErrorNote, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { useRole } from '@/store/auth';
import type { Notice } from '@/types';

/** 공지사항 — 명세서 §5.4 */
export function NoticeListPage() {
  const role = useRole();
  const isStaff = role === ROLES.DIRECTOR || role === ROLES.TEACHER;
  // 31번째 이후 공지가 영영 안 보이던 단발 조회를 커서 페이지네이션으로
  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['notices'],
      initialPageParam: undefined as string | undefined,
      queryFn: ({ pageParam }) =>
        api<Notice[]>(`/notices?limit=30${pageParam ? `&cursor=${pageParam}` : ''}`),
      getNextPageParam: (last) => last.meta?.nextCursor ?? undefined,
    });

  const notices = data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <Screen withNav={false}>
      <AppBar
        title="공지사항"
        back
        right={
          isStaff && (
            <Link to="/records/notices/new" className="text-sm font-semibold text-primary-strong">
              공지 쓰기
            </Link>
          )
        }
      />

      {isPending && <Spinner />}
      {isError && <ErrorNote message="공지를 불러오지 못했어요." onRetry={() => void refetch()} />}
      {!isPending && notices.length === 0 && (
        <EmptyState title={EMPTY_STATES.notice.title} body={EMPTY_STATES.notice.body} />
      )}

      <ul className="flex flex-col gap-3 p-4">
        {notices.map((notice) => (
          <Card key={notice.id} as="li" className="p-0">
            <Link to={`/notices/${notice.id}`} className="block p-4">
              <div className="mb-1 flex flex-wrap items-center gap-1.5">
                {notice.isPinned && <Pin size={14} className="text-ink-sub" aria-label="고정" />}
                {/* 긴급 공지에만 notice 레드를 최소로 쓴다 (§7.2) */}
                {notice.isUrgent && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-notice/10 px-2 py-0.5 text-[12px] font-semibold text-notice">
                    <TriangleAlert size={12} /> 긴급
                  </span>
                )}
                {notice.mustRead && <Chip tone="accent">필독</Chip>}
                {notice.classRoom && <Chip tone="primary">{notice.classRoom.name}</Chip>}
                {!notice.readByMe && <Chip tone="secondary">새 글</Chip>}
              </div>

              <p className="font-semibold">{notice.title}</p>
              <p className="line-clamp-2 text-base text-ink-sub">{notice.content}</p>
              <p className="mt-1.5 text-sm text-ink-sub">{formatDate(notice.publishedAt)}</p>
            </Link>
          </Card>
        ))}
      </ul>

      {hasNextPage && (
        <div className="px-4 pb-6">
          <Button
            variant="outline"
            block
            loading={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            더 보기
          </Button>
        </div>
      )}
    </Screen>
  );
}
